import { Color, ColorManagement } from 'three';

import { MATERIAL_PROPERTIES } from './constants';
import type {
  ColorNumber,
  ColorHexString,
  ColorRGBA,
} from './contracts/colors';

//See https://discourse.threejs.org/t/updates-to-color-management-in-three-js-r152/50791
//and https://github.com/mrdoob/three.js/wiki/Migration-Guide#151--152
//r152 introduced default ouputColorSpace of `sRGB`. This broke the look of colors.
//Out of color management before creating colors!
ColorManagement.enabled = false; //Opting out of color management

export type ColorRGBArray = [number, number, number]; //32 bit precision floats for RGB values between 0 and 1; (Not 8 bit unsigned integers)
export type ColorRGBAArray = [number, number, number, number]; //32 bit precision floats for RGBA values between 0 and 1; (Not 8 bit unsigned integers)

export const defaultColor: Color = new Color(MATERIAL_PROPERTIES.COLOR);
const defaultHexEntry: [number, Color] = [defaultColor.getHex(), defaultColor];
const defaultHexStringEntry: [string, Color] = [
  defaultColor.getHexString(),
  defaultColor,
];

//A private cache of colors by their hexadecimal value
const colorHexCache = new Map<number | string, Color>([
  defaultHexEntry,
  defaultHexStringEntry,
]);

const colorArrayByColor = new Map<Color, ColorRGBArray>([
  [defaultColor, defaultColor.toArray() as ColorRGBArray],
]);

export type ColorChoice =
  | Color
  | ColorNumber
  | ColorHexString
  | ColorRGBA
  | null
  | undefined;

//A getter/factory for Three.Color objects
//* Reuses color if it exists already (saving memory)
export const getOrCreateThreeColor = (color?: ColorChoice): Color => {
  let threeColor: Color | undefined | false;
  threeColor =
    color instanceof Color
      ? color
      : colorHexCache.get(color as number | string) ||
        (typeof color === 'string' &&
          color.startsWith('#') &&
          colorHexCache.get(color.substring(1))); //our cache assumes no '#' prefix
  if (threeColor) {
    return threeColor;
  }

  threeColor =
    Array.isArray(color) || color instanceof Float32Array
      ? new Color(color[0], color[1], color[2]) //RGB values between 0 and 1
      : color != null && color !== MATERIAL_PROPERTIES.COLOR
      ? //color is number (hexadecimal number), or string (RGB, HSL or X11 color string)
        new Color(color)
      : undefined;

  if (threeColor) {
    const hexValue = threeColor.getHex();
    if (colorHexCache.has(hexValue)) {
      //discard threeColor and reuse value from cache
      return colorHexCache.get(hexValue) as Color;
    }
    colorHexCache.set(hexValue, threeColor);
    colorHexCache.set(threeColor.getHexString(), threeColor);
    return threeColor;
  }

  return defaultColor;
};

//A getter/factory for color as array of r, g, b values scaled between 0 and 1
//* Useful for setting 'color' attribute (vertexColors) of BufferGeometry
export const getOrCreateThreeColorArray = (
  _color?: ColorChoice,
): ColorRGBArray => {
  const color = getOrCreateThreeColor(_color);
  let colorArray = colorArrayByColor.get(color) as ColorRGBArray | undefined;
  if (!colorArray) {
    colorArray = color.toArray() as ColorRGBArray;
    colorArrayByColor.set(color, colorArray);
  }
  return colorArray;
};

export const defaultColorArray = getOrCreateThreeColorArray(defaultColor);

export const clearThreeColorCache = (): void => {
  colorHexCache.clear();
  colorArrayByColor.clear();
  colorHexCache.set(...defaultHexEntry);
  colorHexCache.set(...defaultHexStringEntry);
};
